/**
 * The upload wizard — M6 §5 steps 1-4.
 *
 * This file is the orchestration: the reducer (`manager.ts`) decides *what* may happen, the
 * four step components render it, and everything with a side effect lives here.
 *
 * Three things are deliberate:
 *
 * **The `AbortController` is a ref, not state.** Pause has to abort the request that is in
 * flight right now; a controller kept in state would be a stale closure by the time the
 * button is clicked, and pausing would abort nothing.
 *
 * **Metadata is posted on the way out of steps 2 and 3, not only at Submit.** The upload
 * session is the server's record of this form; a reader who fills it in, wanders off and
 * comes back to a refreshed tab must find their words still there, so every step boundary
 * is a save. The post is fire-and-forget — a failed save toasts but never blocks the step,
 * because the reader can still submit (which saves again).
 *
 * **The row is polled while the checks run.** Validation starts at finalize, on a worker, so
 * the detected CRS/extent appear in step 2 a few seconds after the transfer ends. The poll
 * stops as soon as the row carries checks, so a finished upload costs nothing.
 */
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { Btn } from '../../components/Btn';
import {
  deleteUpload,
  fetchConfig,
  fetchDataset,
  fetchDatasetsAll,
  fetchUpload,
  queryKeys,
  saveUploadMetadata,
  type UploadRow,
} from '../../lib/api';
import { toast } from '../../lib/toast';
import {
  resumeUpload,
  startUpload,
  type UploadHandle,
  type UploadProgress,
} from '../../lib/upload';
import { ClassificationStep } from './ClassificationStep';
import { Dropzone } from './Dropzone';
import { MetadataForm } from './MetadataForm';
import { ReviewStep } from './ReviewStep';
import { ReplaceTargetStep } from './ReplaceTargetStep';
import { seedSubmitter, writeSubmitter } from './submitter';
import {
  MAX_UPLOAD_BYTES,
  canProceed,
  initialWizardState,
  prefillFromRecord,
  isAcceptedFile,
  kindHintFor,
  metadataPayload,
  versionTargets,
  wizardReducer,
  type MetadataForm as MetadataFormValues,
  type WizardState,
  type WizardStep,
} from './manager';

/** How often the wizard re-reads the row while the checks run, and for how long at most. */
const POLL_EVERY_MS = 5000;
const POLL_LIMIT_MS = 10 * 60 * 1000;

const STEP_LABELS: Record<WizardStep, string> = {
  1: 'manager.steps.one',
  2: 'manager.steps.two',
  3: 'manager.steps.three',
  4: 'manager.steps.four',
};

export interface UploadWizardProps {
  /** Called with the upload id once it is submitted — the page switches to the queue. */
  onSubmitted: (uploadId: number) => void;
  /** The Datasets tab's "New version of…" handoff: the dataset this upload will version. */
  presetDatasetId?: string | null;
  /**
   * Which rail button opened the wizard (client, 2026-09-18).
   *
   * `new` is the wizard as it was. `replace` puts one mandatory step in front of it — *which
   * dataset?* — because the target is part of `POST /uploads` and a reader who came here to
   * replace something has already decided what: asking first is shorter than offering a
   * select above a dropzone they have to remember to use.
   */
  mode?: 'new' | 'replace';
}

export function UploadWizard({
  onSubmitted,
  presetDatasetId = null,
  mode = 'new',
}: UploadWizardProps) {
  const { t } = useTranslation();
  const [state, dispatch] = useReducer(wizardReducer, presetDatasetId, (preset) =>
    // The remembered submitter is seeded into the first render rather than patched in by an
    // effect: the create call can leave before an effect has run, and it carries the pair.
    seedSubmitter(initialWizardState(preset)),
  );
  const [submitting, setSubmitting] = useState(false);
  /**
   * Has the replace flow's first step been answered? A handoff from the Datasets tab arrives
   * with the answer already in it, so that route skips the step rather than asking a reader
   * to re-confirm the dataset they just clicked.
   */
  const [targetDone, setTargetDone] = useState(mode !== 'replace' || presetDatasetId !== null);
  const abortRef = useRef<AbortController | null>(null);
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      // Leaving the page cancels the transfer in flight; the session and its parts stay on
      // the server, so the reader can resume from the queue rather than start again.
      abortRef.current?.abort();
    };
  }, []);

  // A handoff that arrives while the wizard is already mounted (the Datasets tab is a tab,
  // not a route) has to reach the state too — the lazy initialiser only runs once.
  useEffect(() => {
    if (presetDatasetId) dispatch({ type: 'version', datasetId: presetDatasetId });
  }, [presetDatasetId]);

  const config = useQuery({ queryKey: queryKeys.config(), queryFn: fetchConfig });

  /**
   * The target's own record, for the Replace flow (client, 2026-09-19: *"he should not
   * re-enter the metadata from scratch, it should pre-populate with the existing metadata"*).
   * Read once the target is known, and copied onto the form once per target: the submitter
   * then edits what changed rather than retyping what did not. Picking a different target
   * (Back to the first step) copies again — the form describes *that* dataset now.
   */
  const target = useQuery({
    queryKey: queryKeys.dataset(state.versionOf ?? ''),
    queryFn: fetchDataset,
    enabled: mode === 'replace' && state.versionOf !== null,
  });
  const prefilledFor = useRef<string | null>(null);
  useEffect(() => {
    const rec = target.data;
    if (mode !== 'replace' || !rec || rec.id !== state.versionOf) return;
    if (prefilledFor.current === rec.id) return;
    prefilledFor.current = rec.id;
    dispatch({ type: 'form', patch: prefillFromRecord(rec) });
  }, [mode, state.versionOf, target.data]);
  /**
   * The **managed** catalogue, not the public one: "This is a new version of…" may only
   * offer datasets the manager itself published (`deletable`), and an unpublished dataset is
   * still a legitimate target — `GET /datasets` alone would hide it. `versionTargets` does
   * the narrowing, including by the picked file's type.
   */
  const catalogue = useQuery({ queryKey: queryKeys.datasetsAll(), queryFn: fetchDatasetsAll });

  const { uploadId, phase } = state;
  /**
   * Is there still something to wait for?
   *
   * The validator is a Celery task that starts at finalize, and `pending` is the only status
   * it can still be working under — a row that has reached `review` (or `failed`) is
   * finished, checks or no checks. `POLL_LIMIT_MS` is the backstop for the case the task
   * never lands at all: a dead worker must not leave a tab polling until it is closed.
   */
  const checksPending = state.row === null || state.row.status === 'pending';
  const startedAt = useRef(Date.now());
  useEffect(() => {
    startedAt.current = Date.now();
  }, [uploadId]);
  const rowQuery = useQuery({
    queryKey: queryKeys.upload(uploadId ?? 0),
    queryFn: fetchUpload,
    enabled: uploadId !== null && phase === 'ready' && checksPending,
    refetchInterval: (query) =>
      Date.now() - startedAt.current > POLL_LIMIT_MS ||
      (query.state.data && query.state.data.status !== 'pending')
        ? false
        : POLL_EVERY_MS,
  });

  useEffect(() => {
    if (rowQuery.data) dispatch({ type: 'row', row: rowQuery.data });
  }, [rowQuery.data]);

  /** Wire one transfer's promises into the reducer. */
  const track = useCallback((handle: UploadHandle) => {
    handle.id.then(
      (id) => aliveRef.current && dispatch({ type: 'session', id }),
      () => {},
    );
    handle.done.then(
      (row: UploadRow) => aliveRef.current && dispatch({ type: 'finished', row }),
      (err: unknown) => {
        // A pause aborts the request on purpose — that is not a failure to report.
        if ((err as Error)?.name === 'AbortError' || !aliveRef.current) return;
        dispatch({ type: 'error', message: 'upload' });
      },
    );
  }, []);

  const onPick = useCallback(
    (file: File) => {
      dispatch({ type: 'file', file });
      // The same two rules the reducer applies, read straight from the helpers it uses: an
      // unsupported or oversize file becomes an error in the dropzone and never a session.
      if (!isAcceptedFile(file.name) || file.size > MAX_UPLOAD_BYTES) return;
      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      track(
        startUpload(file, {
          signal: ctrl.signal,
          datasetId: state.versionOf,
          kindHint: kindHintFor(file.name),
          submitterName: state.form.submitterName.trim(),
          submitterEmail: state.form.submitterEmail.trim(),
          onProgress: (progress) => aliveRef.current && dispatch({ type: 'progress', progress }),
        }),
      );
    },
    [state, track],
  );

  const onPause = useCallback(() => {
    abortRef.current?.abort();
    dispatch({ type: 'pause' });
  }, []);

  const onResume = useCallback(() => {
    if (!state.file) return;
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    dispatch({ type: 'resume' });
    const common = {
      signal: ctrl.signal,
      datasetId: state.versionOf,
      kindHint: kindHintFor(state.file.name),
      submitterName: state.form.submitterName.trim(),
      submitterEmail: state.form.submitterEmail.trim(),
      onProgress: (progress: UploadProgress) =>
        aliveRef.current && dispatch({ type: 'progress', progress }),
    };
    // With a session the server can say where it got to; without one there is nothing to
    // resume from and the only honest thing is to start again.
    track(
      state.uploadId !== null
        ? resumeUpload(state.uploadId, state.file, common)
        : startUpload(state.file, common),
    );
  }, [state.file, state.form, state.uploadId, state.versionOf, track]);

  const onCancel = useCallback(() => {
    abortRef.current?.abort();
    if (state.uploadId !== null) {
      // Best effort: the sweeper removes an abandoned session anyway (§1).
      void deleteUpload(state.uploadId).catch(() => {});
    }
    dispatch({ type: 'cancel' });
  }, [state.uploadId]);

  /**
   * Save the form against the session. Never awaited by navigation — see the module
   * docstring — but a failure is said out loud rather than swallowed.
   */
  const save = useCallback((current: WizardState) => {
    if (current.uploadId === null) return Promise.resolve();
    return saveUploadMetadata(current.uploadId, metadataPayload(current)).then(
      (row) => {
        if (aliveRef.current && row) dispatch({ type: 'row', row });
      },
      (err: Error) => {
        if (aliveRef.current) toast(String(err?.message ?? err));
      },
    );
  }, []);

  const leavingSaves = state.step === 2 || state.step === 3;

  const onNext = useCallback(() => {
    if (!canProceed(state.step, state)) return;
    if (leavingSaves) void save(state);
    dispatch({ type: 'next' });
  }, [leavingSaves, save, state]);

  const onBack = useCallback(() => {
    // Back out of step 1 in the replace flow is back to "which dataset?", not a no-op: that
    // step is the one the reader is most likely to want to change their mind about.
    if (state.step === 1 && mode === 'replace') {
      setTargetDone(false);
      return;
    }
    if (leavingSaves) void save(state);
    dispatch({ type: 'back' });
  }, [leavingSaves, mode, save, state]);

  const onSubmit = useCallback(() => {
    if (state.uploadId === null || submitting) return;
    const id = state.uploadId;
    setSubmitting(true);
    writeSubmitter({
      name: state.form.submitterName.trim(),
      email: state.form.submitterEmail.trim(),
    });
    saveUploadMetadata(id, metadataPayload(state))
      .then(() => {
        if (!aliveRef.current) return;
        toast(t('manager.toast.submitted'));
        onSubmitted(id);
      })
      .catch((err: Error) => {
        if (aliveRef.current) toast(t('manager.err.save', { message: err?.message ?? '' }));
      })
      .finally(() => {
        if (aliveRef.current) setSubmitting(false);
      });
  }, [onSubmitted, state, submitting, t]);

  const domains = config.data?.domains ?? [];
  const datasets = useMemo(
    () => versionTargets(catalogue.data?.results ?? [], state.file?.name ?? null),
    [catalogue.data, state.file],
  );
  const onForm = useCallback(
    (patch: Partial<MetadataFormValues>) => dispatch({ type: 'form', patch }),
    [],
  );
  const onOffered = useCallback((value: string) => dispatch({ type: 'resolutionOffered', value }), []);

  const replacing = mode === 'replace';
  /** The target step is showing — the wizard's own four steps are still ahead. */
  const onTargetStep = replacing && !targetDone;

  return (
    <section className="mgr-wizard" data-testid="mgr-wizard">
      <ol className="mgr-stepper" aria-label={t('manager.steps.of', { n: state.step })}>
        {replacing ? (
          <li
            className={onTargetStep ? 'on' : 'done'}
            aria-current={onTargetStep ? 'step' : undefined}
          >
            <span className="mgr-stepper-n">0</span>
            <span className="mgr-stepper-l">{t('manager.steps.target')}</span>
          </li>
        ) : null}
        {([1, 2, 3, 4] as WizardStep[]).map((n) => (
          <li
            key={n}
            className={
              onTargetStep ? '' : n === state.step ? 'on' : n < state.step ? 'done' : ''
            }
            aria-current={!onTargetStep && n === state.step ? 'step' : undefined}
          >
            <span className="mgr-stepper-n">{n}</span>
            <span className="mgr-stepper-l">{t(STEP_LABELS[n])}</span>
          </li>
        ))}
      </ol>

      {onTargetStep ? (
        <ReplaceTargetStep
          datasets={datasets}
          value={state.versionOf}
          onPick={(datasetId) => dispatch({ type: 'version', datasetId })}
        />
      ) : null}

      {!onTargetStep && state.step === 1 ? (
        <Dropzone
          state={state}
          datasets={datasets}
          onPick={onPick}
          onPause={onPause}
          onResume={onResume}
          onCancel={onCancel}
          onForm={onForm}
        />
      ) : null}
      {!onTargetStep && state.step === 2 ? (
        <MetadataForm
          state={state}
          domains={domains}
          onChange={onForm}
          onOffered={onOffered}
          prefilledFrom={replacing && target.data?.id === state.versionOf ? target.data.title : null}
        />
      ) : null}
      {!onTargetStep && state.step === 3 ? (
        <ClassificationStep state={state} onChange={onForm} />
      ) : null}
      {!onTargetStep && state.step === 4 ? (
        <ReviewStep state={state} domains={domains} datasets={datasets} />
      ) : null}

      <footer className="mgr-wizard-foot">
        <Btn
          data-testid="mgr-back"
          disabled={onTargetStep || (state.step === 1 && !replacing)}
          onClick={onBack}
        >
          {t('manager.nav.back')}
        </Btn>
        <span className="mgr-stepcount">
          {onTargetStep ? t('manager.steps.target') : t('manager.steps.of', { n: state.step })}
        </span>
        {onTargetStep ? (
          <Btn
            variant="primary"
            data-testid="mgr-next"
            disabled={state.versionOf === null}
            onClick={() => setTargetDone(true)}
          >
            {t('manager.nav.next')}
          </Btn>
        ) : state.step === 4 ? (
          <Btn
            variant="primary"
            data-testid="mgr-submit"
            disabled={submitting || state.uploadId === null}
            onClick={onSubmit}
          >
            {submitting ? t('manager.review.submitting') : t('manager.review.submit')}
          </Btn>
        ) : (
          <Btn
            variant="primary"
            data-testid="mgr-next"
            disabled={!canProceed(state.step, state)}
            onClick={onNext}
          >
            {t('manager.nav.next')}
          </Btn>
        )}
      </footer>
    </section>
  );
}

export default UploadWizard;
